/* eslint-disable no-case-declarations */
// Helpers
import { round, getPriceExclTax, getPriceInclTax, checkIfShippingIsFree } from '../../helpers/cart-pricing'
import { getValidProducts } from '../../helpers/products-helper'

import DiscountTypes from '../../../../core/enums/discounts'

// Other
import filter from 'lodash/filter'
import sumBy from 'lodash/sumBy'
import isNil from 'lodash/isNil'
import each from 'lodash/each'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'

import './lang'
import FulfillmentTypes from '../../../../core/enums/fulfillment-types'

/**
 * Calculates all the product prices in two different groups: Taxable and non-taxable.
 * This is important for the discount calculation.
 *
 * @memberof CartSummaryComponent
 */
const getTaxableAmountModel = products => {
  const total = {
    taxableAmount: 0,
    nonTaxableAmount: 0
  }

  each(products, product => {
    const { productInfo } = product
    if (productInfo.is_taxable) {
      total.taxableAmount += (productInfo.tax_ex_price || productInfo.price) * product.quantity
    } else {
      total.nonTaxableAmount += (productInfo.tax_ex_price || productInfo.price) * product.quantity
    }
  })

  return total
}

/**
 * Returns the total shipping costs for the current cart.
 *
 * @memberof CartSummaryComponent
 */
const getShippingCosts = (shippingFirstItem, shippingAdditionalItem, shippingRateType, cartContent) => {
  // Get total products, so 2 of the same product, will add also additional shipping costs
  const shippableProducts = filter(cartContent, item => !isNil(item.fulfillmentMethodSelected)
    ? item.fulfillmentMethodSelected === FulfillmentTypes.SHIPPABLE
    : item.productInfo.is_shippable === true)

  if (shippingRateType === 2) {
    return sumBy(shippableProducts, item => get(item, 'productInfo.shipping_charge', 0) * item.quantity)
  } else {
    const count = sumBy(shippableProducts, item => item.quantity)
    if (count === 0) {
      return 0
    }

    if (count === 1) {
      return shippingFirstItem
    }

    if (count >= 2) {
      return shippingFirstItem + (count - 1) * shippingAdditionalItem
    }
  }
}

/**
 * Return an object with the discount for taxable and non-taxable group.
 * It will apply the discount proportional on each group.
 * @param {Number}    taxableAmount       The price of all products which are taxable.
 * @param {Number}    nonTaxableAmount    The price of all products which are not taxable.
 * @param {Boolean}   toEu                If the customer is in Europe or not.
 * @memberof CartSummaryComponent
 */
const getDiscountModel = (taxableAmount, nonTaxableAmount, fromEu, discounts, products, currentTaxPercent) => {
  const discountModel = {
    discountTaxableItems: 0,
    discountNonTaxableItems: 0,
    total: 0
  }

  // Check for empty discount response
  if (!discounts || !discounts.length) {
    return discountModel
  }

  const priceDiscount = discounts.find(discount => discount.couponType !== DiscountTypes.FREE_SHIPPING)

  if (!priceDiscount || isEmpty(priceDiscount)) {
    return discountModel
  }

  const { couponType, amount } = priceDiscount

  // No coupon type? Return
  if (isNil(couponType)) {
    return discountModel
  }

  switch (couponType) {
    case DiscountTypes.PERCENTAGE:
    // Convert to percentage
      const discountPercentage = amount / 100

      each(products, product => {
        const { productInfo } = product

        const unitPrice = productInfo.tax_ex_price || productInfo.price
        let productPrice = unitPrice
        if (fromEu) {
          productPrice = getPriceInclTax(unitPrice, true, currentTaxPercent, fromEu)
        }

        // Multiply with quantity and discountPercentage
        productPrice = productPrice * product.quantity * discountPercentage

        if (productInfo.is_taxable) {
          discountModel.discountTaxableItems += productPrice
        } else {
          discountModel.discountNonTaxableItems += productPrice
        }
      })

      break
    case DiscountTypes.PRICE:
      const productamount = taxableAmount + nonTaxableAmount
      if (productamount > 0) {
        const minAmount = Math.min(amount, productamount)
        discountModel.discountTaxableItems += minAmount * (taxableAmount / productamount)
        discountModel.discountNonTaxableItems += minAmount * (nonTaxableAmount / productamount)
      }

      break
  }

  discountModel.total = discountModel.discountNonTaxableItems + discountModel.discountTaxableItems

  return discountModel
}

/**
 * This method will calculate all the prices for the current cart and returns an object with all the prices.
 *
 * @memberof CartSummaryComponent
 */
export const getCartPrices = ({
  cartContent,
  shippingFirstItem,
  shippingAdditionalItem,
  shippingRateType,
  fromEu,
  currentTaxPercent,
  automaticTaxes,
  minimalAmountForFreeShipping,
  calculateTaxOverShipping,
  taxOriginal,
  pricesInclTax,
  discounts
}) => {
  const result = {
    isTaxIncludedInPrice: pricesInclTax,
    calculateTaxOverShipping,
    tax: 0,
    fromEu,
    automaticTaxes
  }

  const products = getValidProducts(cartContent)

  const { taxableAmount, nonTaxableAmount } = getTaxableAmountModel(products)

  result.subtotalProductsExcl = taxableAmount + nonTaxableAmount
  result.subtotalProductsIncl =
    getPriceInclTax(taxableAmount, true, currentTaxPercent, fromEu, false) + nonTaxableAmount

  const discount = result.fromEu
    ? getDiscountModel(
      getPriceInclTax(taxableAmount, true, currentTaxPercent, fromEu, false),
      nonTaxableAmount,
      result.fromEu,
      discounts,
      products
    )
    : getDiscountModel(taxableAmount, nonTaxableAmount, result.fromEu, discounts, products)

  result.discount = discount.discountTaxableItems + discount.discountNonTaxableItems

  const exclDiscount = result.fromEu
    ? getPriceExclTax(discount.discountTaxableItems, currentTaxPercent)
    : discount.discountTaxableItems
  const exclTaxableMinDiscount = taxableAmount - exclDiscount
  const exclNonTaxableMinDiscount = nonTaxableAmount - discount.discountNonTaxableItems

  result.subtotalExcl = Math.max(0, exclTaxableMinDiscount + exclNonTaxableMinDiscount)
  result.subtotalIncl =
    getPriceInclTax(exclTaxableMinDiscount, true, currentTaxPercent, fromEu, false) + exclNonTaxableMinDiscount

  let shippingcosts = getShippingCosts(shippingFirstItem, shippingAdditionalItem, shippingRateType, cartContent)

  if (
    discounts.some(discount => discount.couponType === DiscountTypes.FREE_SHIPPING) ||
    checkIfShippingIsFree(shippingcosts, result, minimalAmountForFreeShipping)
  ) {
    result.shippingForFreeDiscount = true
    shippingcosts = 0
  }

  result.shippingIncl = result.shippingExcl = shippingcosts

  if (calculateTaxOverShipping) {
    if (pricesInclTax) {
      // Calculate shipping price without original tax price from store country
      result.shippingExcl = getPriceExclTax(shippingcosts, taxOriginal)
      result.shippingIncl = getPriceInclTax(result.shippingExcl, true, currentTaxPercent, fromEu, false)
    } else {
      result.shippingIncl = getPriceInclTax(shippingcosts, true, currentTaxPercent, fromEu, false)
    }
  }

  result.shipping = round(fromEu ? result.shippingIncl : result.shippingExcl)
  result.subtotalProducts = round(fromEu ? result.subtotalProductsIncl : result.subtotalProductsExcl)
  result.subtotal = round(fromEu ? result.subtotalIncl : result.subtotalExcl)

  result.tax += result.subtotalIncl - result.subtotalExcl
  result.tax += result.shippingIncl - result.shippingExcl
  result.tax = round(result.tax)

  result.grantTotalExcl = round(result.subtotalExcl + result.shippingExcl)
  result.grantTotalIncl = round(result.grantTotalExcl + result.tax)

  result.discount = round(result.discount)

  return result
}
